"use client";

import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { Badge } from "@/components/common/ui/Badge";
import { Tooltip } from "@/components/common/ui/Tooltip";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { ReportButton } from "@/components/reports/ReportButton";
import { CarrierReportModal } from "./CarrierReportModal";
import {
  CarrierLogo,
  CarrierName,
  InlineReRateButton,
  PortalLinkButton,
  ReRateButton,
} from "./CarrierTileParts";
import type { CompactCarrierRowProps, CompactRowVariant } from "./types";

interface VariantConfig {
  borderClass: string;
  summaryColor: string; // Typography color token (tailwind class OR named)
  badgeVariant: "error" | "warning" | "neutral";
  badgeIcon: LucideIcon;
  defaultBadgeLabel: string;
  defaultTitle: string;
  titleIcon: LucideIcon;
  titleColor: string; // bg color tailwind class for wrapper box
  boxClass: string; // e.g. "bg-red-50 border-red-200"
  headingColor: string;
  descriptionColor: string;
  itemsColor: string;
}

const VARIANTS: Record<CompactRowVariant, VariantConfig> = {
  error: {
    borderClass: "border-red-300",
    summaryColor: "error",
    badgeVariant: "error",
    badgeIcon: AlertCircle,
    defaultBadgeLabel: "Error",
    defaultTitle: "Error",
    titleIcon: AlertCircle,
    titleColor: "text-red-600",
    boxClass: "bg-red-50 border-red-200",
    headingColor: "text-red-900",
    descriptionColor: "text-red-700",
    itemsColor: "text-red-700",
  },
  referral: {
    borderClass: "border-amber-300",
    summaryColor: "text-amber-700",
    badgeVariant: "warning",
    badgeIcon: AlertTriangle,
    defaultBadgeLabel: "Referral",
    defaultTitle: "Requires Underwriter Review",
    titleIcon: AlertTriangle,
    titleColor: "text-amber-600",
    boxClass: "bg-amber-50 border-amber-200",
    headingColor: "text-amber-800",
    descriptionColor: "text-amber-800",
    itemsColor: "text-amber-700",
  },
  declined: {
    borderClass: "border-orange-300",
    summaryColor: "text-orange-700",
    badgeVariant: "warning",
    badgeIcon: Ban,
    defaultBadgeLabel: "Declined",
    defaultTitle: "Decline Reason",
    titleIcon: Ban,
    titleColor: "text-orange-600",
    boxClass: "bg-orange-50 border-orange-200",
    headingColor: "text-orange-800",
    descriptionColor: "text-orange-800",
    itemsColor: "text-orange-700",
  },
  unavailable: {
    borderClass: "border-slate-300",
    summaryColor: "text-slate-600",
    badgeVariant: "neutral",
    badgeIcon: ShieldAlert,
    defaultBadgeLabel: "Unavailable",
    defaultTitle: "Carrier Unavailable",
    titleIcon: ShieldAlert,
    titleColor: "text-slate-600",
    boxClass: "bg-slate-50 border-slate-200",
    headingColor: "text-slate-800",
    descriptionColor: "text-slate-700",
    itemsColor: "text-slate-600",
  },
};

/**
 * Compact row used by the CarrierList bucket sections for non-main carriers
 * (errors, referrals with no premium, declined/rejected, unavailable).
 *
 * Layout mirrors CarrierCard's main row (checkbox spacer, chevron, logo, name)
 * but replaces the premium / commission / quote-id columns with a single
 * summary string, a status Badge, and the re-rate affordance.
 */
export function CompactCarrierRow({
  carrier,
  variant,
  isSelected = false,
  onToggle,
  hideSelection = false,
  selectionDisabled = false,
  selectionDisabledReason,
  summary,
  badgeLabel,
  badgeVariant,
  badgeIcon,
  title,
  description,
  items,
  aiSummarized,
  link,
  reRateAction,
  report,
  reportDisabledReason,
  expandedContent,
}: CompactCarrierRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const cfg = VARIANTS[variant];

  // An error row's report is already on its way to us, so it stays disabled;
  // reportDisabledReason lets a caller disable it for its own reason. Either
  // way the modal is not mounted, so no report can be sent.
  const reportDisabled = variant === "error" || !!reportDisabledReason;

  const resolvedSummary = summary ?? cfg.defaultTitle;
  const resolvedBadgeLabel = badgeLabel ?? cfg.defaultBadgeLabel;
  const resolvedBadgeVariant = badgeVariant ?? cfg.badgeVariant;
  const resolvedBadgeIcon = badgeIcon ?? cfg.badgeIcon;
  const resolvedTitle = title ?? cfg.defaultTitle;

  const hasExpandedContent =
    expandedContent !== null && expandedContent !== undefined;
  const hasExpandableContent = !!(
    description ||
    (items && items.length > 0) ||
    hasExpandedContent
  );
  const hasVariantDetail = !!(description || (items && items.length > 0));
  const hasExpandedFooterActions = !!(
    reRateAction ||
    (link && variant === "referral")
  );

  return (
    <div
      className={cn("@container bg-white rounded-lg border transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] overflow-hidden", cfg.borderClass)}
    >
      {/* Main row — matches CarrierCard layout */}
      <div
        className={cn(
          "flex items-center gap-4 px-4 py-3 select-none",
          hasExpandableContent && "cursor-pointer",
        )}
        onClick={
          hasExpandableContent ? () => setExpanded((prev) => !prev) : undefined
        }
      >
        {onToggle && !hideSelection ? (
          <Tooltip
            content={selectionDisabledReason ?? `Select or deselect ${carrier.name}`}
          >
            <div
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={selectionDisabled}
              aria-label={`${isSelected ? "Deselect" : "Select"} ${carrier.name}`}
              tabIndex={selectionDisabled ? -1 : 0}
              onClick={(event) => {
                event.stopPropagation();
                if (!selectionDisabled) onToggle();
              }}
              onKeyDown={(event) => {
                if (!selectionDisabled && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  event.stopPropagation();
                  onToggle();
                }
              }}
              className={cn(
                "w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors",
                selectionDisabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer",
                isSelected
                  ? "bg-blue-500 border-blue-500"
                  : "border-gray-300 hover:border-blue-400",
              )}
            >
              {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
            </div>
          </Tooltip>
        ) : (
          <div className="w-5 flex-shrink-0" />
        )}

        {/* Expand chevron */}
        <div className="flex-shrink-0 text-gray-400 w-4">
          {hasExpandableContent &&
            (expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            ))}
        </div>

        {/* Carrier logo — hidden when container is very narrow */}
        <CarrierLogo carrier={carrier} className="hidden @3xl:flex" />

        {/* Carrier name */}
        <CarrierName carrier={carrier} />

        {/* Report a Problem — between name and summary */}
        {report && (
          <>
            <div className="flex-shrink-0 w-px h-6 bg-gray-200" />
            <ReportButton
              size="sm"
              label="Report"
              disabled={reportDisabled}
              disabledTooltip={reportDisabledReason ?? "Sent to Gencise"}
              onClick={(e) => {
                e.stopPropagation();
                setReportModalOpen(true);
              }}
            />
            {!reportDisabled && (
              <CarrierReportModal
                isOpen={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                applicationId={report.applicationId}
                carrierName={report.carrierName}
                carrierDisplayName={report.carrierDisplayName}
                coverageType={report.coverageType}
                quoteData={report.quoteData}
                vertical={report.vertical}
                taskId={report.taskId}
                initialComments={report.initialComments}
              />
            )}
            <div className="flex-shrink-0 w-px h-6 bg-gray-200" />
          </>
        )}

        {/* Summary text */}
        <Typography
          as="span"
          variant="body"
          weight="medium"
          color={cfg.summaryColor}
          truncate
          className="flex-1"
        >
          {resolvedSummary}
        </Typography>

        {/* Portal link (referrals) — hidden at small container */}
        {link && variant === "referral" && (
          <PortalLinkButton
            link={link}
            className="flex-shrink-0 hidden @2xl:inline-flex"
          />
        )}

        {/* Status badge */}
        <div className="w-24 flex-shrink-0 flex justify-center ml-auto">
          <Badge variant={resolvedBadgeVariant} size="sm" icon={resolvedBadgeIcon}>
            {resolvedBadgeLabel}
          </Badge>
        </div>

        {/* Divider + Re-rate — hidden at small container */}
        {reRateAction && (
          <div className="hidden @3xl:contents">
            <ReRateButton
              onReRate={reRateAction.onClick}
              disabled={reRateAction.disabled}
              title={reRateAction.title}
            />
          </div>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && hasExpandableContent && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50">
          {hasVariantDetail && (
            <div
              className={cn(
                "border rounded-lg p-3",
                cfg.boxClass,
                (hasExpandedContent || hasExpandedFooterActions) && "mb-3",
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <cfg.titleIcon className={cn("w-4 h-4", cfg.titleColor)} />
                <Typography
                  as="span"
                  variant="body"
                  weight="semibold"
                  color={cfg.headingColor}
                >
                  {resolvedTitle}
                </Typography>
                {aiSummarized && (
                  <Tooltip content="AI-classified status" placement="top">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400 cursor-default" />
                  </Tooltip>
                )}
              </div>
              {description && (
                <Typography
                  as="p"
                  variant="body"
                  color={cfg.descriptionColor}
                  className="mb-2"
                >
                  {description}
                </Typography>
              )}
              {items && items.length > 0 && (
                <Typography
                  as="ul"
                  variant="small"
                  color={cfg.itemsColor}
                  leading="leading-relaxed"
                  className="space-y-1"
                >
                  {items.map((item, i) => (
                    <li key={i}>
                      {description ? "\u2022 " : ""}
                      {item}
                    </li>
                  ))}
                </Typography>
              )}
            </div>
          )}

          {hasExpandedContent && (
            <div className={cn(hasExpandedFooterActions && "mb-3")}>
              {expandedContent}
            </div>
          )}

          {/* Footer: Portal Link + Re-rate at narrow breakpoints */}
          {hasExpandedFooterActions && (
            <div className="pt-3 border-t border-gray-200 flex items-center">
              <div className="ml-auto flex items-center gap-2">
                {link && variant === "referral" && (
                  <span className="@2xl:hidden">
                    <PortalLinkButton link={link} />
                  </span>
                )}
                {reRateAction && (
                  <InlineReRateButton
                    onReRate={reRateAction.onClick}
                    disabled={reRateAction.disabled}
                    title={reRateAction.title}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
